import { useDialogs } from "@platform/hooks";
import { useCallback, useMemo } from "react";
import {
  AddPlanDialogData,
  DeletePlanDialogData,
  DialogName,
  DuplicatePlanDialogData,
  NicknamePlanDialogData,
  RfpCoverage,
} from "@platform/plan-grid-core";
import {
  getMainMenuItems as createPlanEditorMainMenuItems,
  getPlanLevelMenuItem as createPlanLevelMenuItem,
  useFeatureFlag,
} from "@platform/plan-grid-core";

import { useListBenefitPlanGroups } from "../api";

import type { GetMainMenuItems } from "ag-grid-community";

export const usePlanEditorMainMenuItems = (
  benefitPlanGroups: ReturnType<typeof useListBenefitPlanGroups>["data"],
  getCurrentCoverage: () => RfpCoverage | undefined,
  planLevelMenuItems: GetMainMenuItems,
): GetMainMenuItems => {
  const { openDialog } = useDialogs<Exclude<DialogName, "comment">>();
  const newDeletePlanValidation = useFeatureFlag("newDeletePlanValidation");

  const {
    openAddPlanDialog,
    openNicknamePlanDialog,
    openDeletePlanDialog,
    openDuplicatePlanDialog,
  } = useMemo(
    () => ({
      openAddPlanDialog: (data: AddPlanDialogData) =>
        openDialog<AddPlanDialogData>("addPlan", data),
      openNicknamePlanDialog: (data: NicknamePlanDialogData) =>
        openDialog<NicknamePlanDialogData>("nickname", data),
      openDeletePlanDialog: (data: DeletePlanDialogData) =>
        openDialog<DeletePlanDialogData>("deletePlan", data),
      openDuplicatePlanDialog: (data: DuplicatePlanDialogData) =>
        openDialog<DuplicatePlanDialogData>("duplicatePlan", data),
    }),
    [openDialog],
  );

  return useCallback<GetMainMenuItems>(
    (params) =>
      createPlanEditorMainMenuItems(params, {
        openAddPlanDialog,
        openNicknamePlanDialog,
        openDeletePlanDialog,
        openDuplicatePlanDialog,
        getCurrentCoverage,
        benefitPlanGroups,
        newDeletePlanValidation,
        planLevelMenuItems,
      }),
    [
      benefitPlanGroups,
      getCurrentCoverage,
      newDeletePlanValidation,
      openAddPlanDialog,
      openDeletePlanDialog,
      openDuplicatePlanDialog,
      openNicknamePlanDialog,
      planLevelMenuItems,
    ],
  );
};

export const usePlanLevelMenuItems = (
  onAddPlanToGroup: (planGroupId: string, currentSize: number) => void,
  onDuplicatePlanInGroup: (
    planGroupId: string,
    benefitPlanId: string,
    currentSize: number,
  ) => void,
): GetMainMenuItems => {
  const { openDialog } = useDialogs<Exclude<DialogName, "comment">>();

  const openDeletePlanLevelDialog = useMemo(
    () => (data: DeletePlanDialogData) =>
      openDialog<DeletePlanDialogData>("deletePlanLevel", data),
    [openDialog],
  );

  return useCallback<GetMainMenuItems>(
    (params) =>
      createPlanLevelMenuItem(params, {
        openDeletePlanLevelDialog,
        onAddPlanToGroup,
        onDuplicatePlanInGroup,
      }),
    [openDeletePlanLevelDialog, onAddPlanToGroup, onDuplicatePlanInGroup],
  );
};
